import { env } from '@/lib/utils/env';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import cookies from 'js-cookie';
import { ChatDto, ScheduledMessageDto, UserMessageDto } from '@/lib/types';
import { socket } from '@/lib/hooks/useWebSocket';
import { SocketEvent } from '@/lib/enums/socket-event.enum';
import { compareChatsByLastMessage } from '@/helpers/compareChatsByLastMessage';
import type { ProfileState } from '@/store/types';

import { applyNewMessageToChats, getOpenChatId } from './apply-new-message';

import { ApiTag } from '../api-tags.enum';
import { HttpMethod } from '../http-method.enum';

export const chatsApi = createApi({
  reducerPath: 'chatsApi',
  tagTypes: [ApiTag.Chat, ApiTag.ChatMessage, ApiTag.ScheduledMessage],
  refetchOnReconnect: false,
  refetchOnFocus: false,
  refetchOnMountOrArgChange: false,
  baseQuery: fetchBaseQuery({
    baseUrl: env.BASE_URL,
    credentials: 'include',
    prepareHeaders: (headers) => {
      const accessToken = cookies.get('access_token');
      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    getAllChats: builder.query<ChatDto[], void>({
      query: () => ({
        url: `/chats`,
        method: HttpMethod.Get,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: ApiTag.Chat as const, id })),
              { type: ApiTag.Chat as const, id: 'LIST' },
            ]
          : [{ type: ApiTag.Chat as const, id: 'LIST' }],
      async onCacheEntryAdded(
        _,
        {
          updateCachedData,
          cacheDataLoaded,
          cacheEntryRemoved,
          dispatch,
          getState,
        },
      ) {
        await cacheDataLoaded;
        const activeSocket = socket;
        const refetchList = () =>
          dispatch(
            chatsApi.util.invalidateTags([{ type: ApiTag.Chat, id: 'LIST' }]),
          );

        // Payload chats carry the patient's unread counts, not ours: refetch.
        const onNewChats = () => refetchList();

        // Keeps the list order, previews and unread badges (inbox, side nav,
        // dashboard) live for every conversation, not only the open one.
        const onNewMessage = (message: UserMessageDto) => {
          const ownUserId = (getState() as unknown as { profile: ProfileState })
            .profile?.session?.id;
          let known = true;
          updateCachedData((draft) => {
            known = applyNewMessageToChats(draft, message, {
              ownUserId,
              openChatId: getOpenChatId(),
            });
          });
          if (!known) {
            refetchList();
          }
        };

        const onDeletePatient = (payload: {
          type: SocketEvent.DeletePatient;
          data: {
            chatsIds: string[];
            userId: string;
          };
        }) => {
          updateCachedData((draft) =>
            draft
              .filter((chat) => !payload.data.chatsIds.includes(chat.id))
              .sort(compareChatsByLastMessage),
          );
        };

        activeSocket?.on(SocketEvent.NewChats, onNewChats);
        activeSocket?.on(SocketEvent.NewMessage, onNewMessage);
        activeSocket?.on(SocketEvent.DeletePatient, onDeletePatient);

        await cacheEntryRemoved;
        activeSocket?.off(SocketEvent.NewChats, onNewChats);
        activeSocket?.off(SocketEvent.NewMessage, onNewMessage);
        activeSocket?.off(SocketEvent.DeletePatient, onDeletePatient);
      },
    }),
    getChatMessages: builder.query<UserMessageDto[], string>({
      query: (id) => ({
        url: `/chats/${id}/messages`,
        method: HttpMethod.Get,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: ApiTag.ChatMessage as const,
                id,
              })),
              { type: ApiTag.ChatMessage as const, id: 'LIST' },
            ]
          : [{ type: ApiTag.ChatMessage as const, id: 'LIST' }],
      async onCacheEntryAdded(
        queryArg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        await cacheDataLoaded;
        // NOTE: care if socket is undefined, fix socket trash
        // Other conversations are kept up to date by getAllChats' own
        // listener; this one only appends to the open conversation.
        const onNewMessage = (message: UserMessageDto) => {
          if (message.chatId !== queryArg) return;
          updateCachedData((draft) => {
            if (!draft.find((msg) => msg.id === message.id)) {
              draft.unshift(message);
            }
          });
        };
        const onMessagesRead = (payload: { chatId: string; messageIds: string[] }) => {
          if (payload.chatId !== queryArg) return;
          const ids = new Set(payload.messageIds);
          updateCachedData((draft) => {
            draft.forEach((message) => {
              if (ids.has(message.id)) message.isRead = true;
            });
          });
        };
        socket!.on(SocketEvent.NewMessage, onNewMessage);
        socket!.on(SocketEvent.MessagesRead, onMessagesRead);

        socket!.on(
          SocketEvent.DeletePatient,
          (payload: {
            type: SocketEvent.DeletePatient;
            data: {
              chatsIds: string[];
              userId: string;
            };
          }) => {
            updateCachedData((draft) => {
              draft.forEach((message, index, self) => {
                if (payload.data.chatsIds.includes(message.chatId)) {
                  self.splice(index, 1);
                }
              });
            });
          },
        );

        await cacheEntryRemoved;
        socket?.off(SocketEvent.NewMessage, onNewMessage);
        socket?.off(SocketEvent.MessagesRead, onMessagesRead);
      },
    }),
    readChatMessages: builder.mutation<void, string>({
      query: (chatId) => ({
        url: `/chats/${chatId}/read`,
        method: HttpMethod.Post,
      }),
      invalidatesTags: (result, error, chatId) => [
        { type: ApiTag.Chat, id: chatId },
      ],
    }),
    uploadFile: builder.mutation<void, { chatId: string; formData: FormData }>({
      query: ({ chatId, formData }) => ({
        url: `/chats/${chatId}/messages`,
        method: HttpMethod.Post,
        body: formData,
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: ApiTag.ChatMessage, id: chatId },
        { type: ApiTag.ChatMessage, id: 'LIST' },
      ],
    }),
    getScheduledMessages: builder.query<ScheduledMessageDto[], string>({
      query: (chatId) => ({ url: `/chats/${chatId}/scheduled-messages?status=pending`, method: HttpMethod.Get }),
      providesTags: (_result, _error, chatId) => [{ type: ApiTag.ScheduledMessage, id: chatId }],
    }),
    createScheduledMessage: builder.mutation<ScheduledMessageDto, { chatId: string; message: string; scheduledAt: string }>({
      query: ({ chatId, ...body }) => ({ url: `/chats/${chatId}/scheduled-messages`, method: HttpMethod.Post, body }),
      invalidatesTags: (_result, _error, { chatId }) => [{ type: ApiTag.ScheduledMessage, id: chatId }],
    }),
    updateScheduledMessage: builder.mutation<ScheduledMessageDto, { chatId: string; id: string; message?: string; scheduledAt?: string }>({
      query: ({ chatId, id, ...body }) => ({ url: `/chats/${chatId}/scheduled-messages/${id}`, method: HttpMethod.Patch, body }),
      invalidatesTags: (_result, _error, { chatId }) => [{ type: ApiTag.ScheduledMessage, id: chatId }],
    }),
    cancelScheduledMessage: builder.mutation<void, { chatId: string; id: string }>({
      query: ({ chatId, id }) => ({ url: `/chats/${chatId}/scheduled-messages/${id}`, method: HttpMethod.Delete }),
      invalidatesTags: (_result, _error, { chatId }) => [{ type: ApiTag.ScheduledMessage, id: chatId }],
    }),
  }),
});

export const {
  useGetAllChatsQuery,
  useGetChatMessagesQuery,
  useReadChatMessagesMutation,
  useUploadFileMutation,
  useGetScheduledMessagesQuery,
  useCreateScheduledMessageMutation,
  useUpdateScheduledMessageMutation,
  useCancelScheduledMessageMutation,
} = chatsApi;
